import { INavLink, INavLinkGroup } from '@fluentui/react';
import { INavigationItem } from '../../view-models/navigation/INavigationItem';

export class UserFavorites {
    static GroupKey = '1b75b091-5754-4ef7-82ad-b235374cde80';
    private _favoredNamesToIdsMap = new Map<string, Set<string>>();
    private _favoredItemsMap = new Map<string, INavLink>();
    private _group: INavLinkGroup;
    private _isGroupExpanded: boolean;
    private _maxFavoriteOrdinal: number;

    constructor(
        private _item: INavigationItem,
        handleHeaderClick: (key: string, isCollapsing?: boolean) => Promise<void>
    ) {
        this._isGroupExpanded = this._item?.displayProperties?.isExpanded ?? false;
        this._group = {
            name: this._item?.displayName,
            expandAriaLabel: `Expand ${this._item?.displayName} section.`,
            onHeaderClick: async (_, isCollapsing) => await handleHeaderClick(this._item.id, isCollapsing),
            links: [],
        };
        this._maxFavoriteOrdinal = 0;
    }

    get favoredGroup(): INavLinkGroup {
        this._group.links = Array.from(this._favoredItemsMap.values()).sort((a, b) => a.favoritesDisplayOrder - b.favoritesDisplayOrder);
        this._group.isExpanded = this._isGroupExpanded;
        return this._group;
    }

    get nextFavoriteOrdinal(): number {
        return this._maxFavoriteOrdinal + 1;
    }

    expandFavorites() {
        this._isGroupExpanded = true;
    }

    collapseFavorites() {
        this._isGroupExpanded = false;
    }

    addItem(item: INavLink, displayOrderOrdinal: number) {
        if (!item.key) {
            return;
        }
        // ensure set
        item.isPinned = true;
        item.favoritesDisplayOrder = displayOrderOrdinal;

        if (this._favoredNamesToIdsMap.has(item.name)) {
            const ids = this._favoredNamesToIdsMap.get(item.name);
            for (const id of Array.from(ids!)) {
                this.renameAndUpdateExistingItem(id);
            }
            ids!.add(item.key);
            this._favoredNamesToIdsMap.set(item.name, ids!);
            this.renameAndSetItem(item);
        } else {
            const ids = new Set<string>();
            ids.add(item.key);
            this._favoredNamesToIdsMap.set(item.name, ids);
            this.setFavoriteItem(item);
        }
    }

    removeItem(item: INavLink) {
        if (!item.key) {
            return;
        }
        // ensure set to false
        item.isPinned = false;

        if (this._favoredItemsMap.has(item.key)) {
            this._favoredItemsMap.delete(item.key);
            if (this._favoredNamesToIdsMap.has(item.name)) {
                // only one since names match - delete
                this._favoredNamesToIdsMap.delete(item.name);
            } else {
                // item was renamed
                if (this._favoredNamesToIdsMap.has(item.originalName)) {
                    const ids = this._favoredNamesToIdsMap.get(item.originalName);
                    ids!.delete(item.key);
                    if (ids!.size === 1) {
                        this.revertNameAndUpdateExistingItem(Array.from(ids!)[0]);
                    }
                }
            }
        }
    }

    private revertNameAndUpdateExistingItem(id: string) {
        const item = this._favoredItemsMap.get(id);
        if (!item) {
            return;
        }
        const { originalName, ...renamedItem } = item;
        renamedItem.name = originalName;
        renamedItem.ariaLabel = originalName;
        this.setFavoriteItem(renamedItem);
    }

    private renameAndUpdateExistingItem(id: string) {
        const item = this._favoredItemsMap.get(id);
        if (!item) {
            return;
        }
        this.renameAndSetItem(item);
    }

    private renameAndSetItem(item: INavLink) {
        const renamedItem = { ...item };
        let uniqueName = item.name;
        if (renamedItem.parentDisplayName) {
            uniqueName = `${item.name} (${renamedItem.parentDisplayName})`;
        }
        renamedItem.name = uniqueName;
        renamedItem.ariaLabel = uniqueName;
        renamedItem.originalName = item.name;
        this.setFavoriteItem(renamedItem);
    }

    private setFavoriteItem(item: INavLink) {
        const { links, expandAriaLabel, ...rest } = item;
        rest.isFavoriteCopy = true;
        this._favoredItemsMap.set(item.key!, rest);
        this._maxFavoriteOrdinal = Math.max(this._maxFavoriteOrdinal, item.favoritesDisplayOrder);
    }
}
